import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

const HERO_IMAGE = "/img/dana2.webp";

const stats = [
/*{ value: "15+", label: "Rokov praxe" },
{ value: "200+", label: "Spokojných klientov" },
{ value: "4.9★", label: "Hodnotenie" },*/
];

export default function Hero() {
const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

return (
  <section
    id="domov"
    data-testid="hero-section"
    className="relative min-h-screen flex items-center overflow-hidden bg-[#1C1C2E] pt-16"
  >
    {/* Background blobs */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#F72585]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/3 left-1/4 w-72 h-72 bg-[#7209B7]/15 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-12 lg:py-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-8 items-center min-h-[85vh]">
          {/* Left: Text */}
          <div className="flex flex-col justify-center items-center text-center lg:items-start lg:text-left">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-block text-xs font-bold tracking-[0.25em] uppercase text-[#F72585] mb-5"
            >
              Certifikovaná fitness trénerka
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              data-testid="hero-headline"
              className="text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-none mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              <span className="text-white block">TRÉNUJ</span>
              <span className="text-white block">SMART</span>
              <span
                className="block"
                style={{
                  background: "linear-gradient(90deg, #F72585, #7209B7)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                ŽI NAPLNO
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-lg text-zinc-400 mb-8 max-w-lg leading-relaxed"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Prevezmi kontrolu nad svojím zdravím a kondíciou s profesionálnym
              coachingom, ktorý spája efektívny tréning s udržateľným životným štýlom.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 mb-12 w-full sm:w-auto"
            >
              <button
                onClick={() => scrollTo("#kontakt")}
                data-testid="hero-cta-primary"
                className="px-8 py-4 bg-[#F72585] hover:bg-[#E01A4F] text-white font-bold rounded-full text-base transition-all duration-300 hover:scale-105"
                style={{ transition: "all 0.3s ease" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.boxShadow = "0 0 30px rgba(247,37,133,0.4)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.boxShadow = "none")
                }
              >
                Začni trénovať
              </button>
              <button
                onClick={() => scrollTo("#o-mne")}
                data-testid="hero-cta-secondary"
                className="px-8 py-4 border-2 border-white/20 hover:border-[#F72585] text-white font-bold rounded-full text-base transition-all duration-300 hover:text-[#F72585]"
              >
                Zisti viac
              </button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="flex gap-8 justify-center lg:justify-start"
            >
              {stats.map((stat) => (
                <div key={stat.label}>
                  <div
                    className="text-2xl font-black text-[#F72585]"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {stat.value}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: Image — hidden on mobile, visible on lg+ */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: "easeOut" }}
            className="hidden lg:flex lg:justify-end"
          >
            <div className="relative">
              {/* Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#F72585]/25 to-[#7209B7]/25 rounded-2xl blur-2xl scale-105 pointer-events-none" />
              <div className="relative overflow-hidden rounded-2xl">
                <img
                  src={HERO_IMAGE}
                  alt="Danka Vysoká - Fitness trénerka"
                  data-testid="hero-image"
                  className="w-full max-w-md h-[75vh] object-cover"
                  style={{ objectPosition: "center top" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C2E]/40 to-transparent" />
              </div>
              {/* Floating badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="absolute -bottom-4 -left-4 bg-[#252540] border border-[#F72585]/30 rounded-xl px-4 py-3 shadow-xl"
              >
                <div
                  className="text-sm font-semibold text-white"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  City Fitness Ruzinov
                </div>
                <div className="text-xs text-zinc-400">Bratislava</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.button
        onClick={() => scrollTo("#o-mne")}
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-zinc-500 hover:text-[#F72585] transition-colors"
        data-testid="hero-scroll-indicator"
      >
        <ChevronDown size={28} />
      </motion.button>

      {/* Bottom gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#1C1C2E] to-transparent pointer-events-none" />
    </section>
  );
}
